// ============================================================================
// Stage 4 pricing rules, shared by the Scope Builder and the QM Assistant
// (spec 2026-10-07 section 5.5, findings F32, F37, F50).
// ----------------------------------------------------------------------------
// The Scope Builder rounds a typed price to whole dollars on an inc-GST quote,
// converts every amount when the GST basis toggles, and applies the HBCF rule
// of the business's own Pricing page method. The Assistant uses the same copy.
// Since build-up pricing step 2 (2026-11-03) no price is re-derived from a cost
// breakdown or a markup: an item's price is its own amount.
// ============================================================================

import {
    calculateHbcfEligibilityBaseIncGst,
    isManagedComplianceRow,
    isManagedHbcfRow,
    managedScopeItemIncludesHbcf,
    projectFrancisHbcf,
    reconcileFrancisStatutoryScope,
} from '../services/rj-bird-pricing-methodology';
import { pricingPageResolvedMethodology } from '../../backend/src/shared/quote-brain/rate-book-core.js';
import { isStage4AmountAuthorityEnabled } from './feature-flags';
import { roundCents } from './assistant-amounts';
import { convertScopePricingDisplayBasis } from './inclusions-helpers';
import { QUOTE_BRAIN_ROW_GENERATOR } from './quote-brain-project';
import { isImportedQuote } from '../services/quote-jobs/imported-quote';

type ScopeItem = Record<string, any>;

function amountNumber(value: unknown): number {
    return parseFloat(String(value ?? '0').replace(/,/g, '')) || 0;
}

/**
 * True when the item's client price is the committed receipt from Pricing.
 * Stage 4 never re-derives such a price from its costs (QW-FIX-6).
 */
export function hasCommittedStage4Price(item: ScopeItem | null | undefined): boolean {
    // A Quote Brain row's amount is pricing-core's receipt (or the builder's
    // own price), whatever the flag says: Stage 4 never re-derives it from
    // costs (Quote Brain DESIGN.md 2.12 W4).
    if (item?.generatedBy === QUOTE_BRAIN_ROW_GENERATOR && item?.amountAuthority === 'committed') return true;
    // So is any item priced from its own lines (quote-brain-buildup.ts), the
    // builder's included: an old cost breakdown never re-derives it.
    const buildUp = item?.quoteBrain?.buildUp;
    if (Array.isArray(buildUp) && buildUp.length > 0) return true;
    return isStage4AmountAuthorityEnabled()
        && ['committed', 'provisional-builder-review'].includes(String(item?.amountAuthority || ''));
}

function audMoney(value: number): string {
    return Number(value || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function isManagedStatutoryRow(item: ScopeItem | null | undefined): boolean {
    return !!item && (isManagedHbcfRow(item) || isManagedComplianceRow(item));
}

/**
 * The pricing method a job's HBCF and compliance rows follow: the business's
 * Pricing page method, or the starter method when it has none of its own
 * (rate-book-core.js pricingPageResolvedMethodology), the method the Quote
 * Brain prices with. Never RJ Bird's card under it: a business without its
 * own HBCF figures used to get RJ Bird's premium rate and fee. Null when
 * Pricing has not loaded (no costing config) or the business's own method
 * has a gap, so the rule cannot be read.
 */
export function jobPricingMethodology(costingConfig: Record<string, any> | null | undefined): Record<string, any> | null {
    if (!costingConfig || typeof costingConfig !== 'object') return null;
    try {
        return pricingPageResolvedMethodology(costingConfig);
    } catch {
        return null;
    }
}

/** The HBCF rule for a job, or null when HBCF is the builder's (waived, or carried in the items) or the rule cannot be read. */
function hbcfRuleContext({
    costingConfig,
    formData,
    isGstInclusive = true,
    compliancePosture = null,
}: { costingConfig?: Record<string, any> | null; formData?: Record<string, any> | null; isGstInclusive?: boolean; compliancePosture?: any }) {
    if (formData?.hbcfWaived === true || formData?.hbcfIncludedInScope === true) return null;
    const methodology: any = jobPricingMethodology(costingConfig);
    if (!methodology?.hbcf) return null;
    const basis = { isGstInclusive: isGstInclusive !== false, gstRate: Number(methodology?.gst_rate ?? methodology?.gstRate) || 0.1 };
    return {
        methodology,
        compliancePosture,
        isGstInclusive: isGstInclusive !== false,
        required: (scope: ScopeItem[]) => projectFrancisHbcf(calculateHbcfEligibilityBaseIncGst(scope, basis), methodology),
    };
}

/**
 * What the HBCF button would do to these items now: whether they have HBCF
 * lines, whether they would after it, by how much the statutory rows would
 * move the total (inc GST), and whether a manual HBCF line blocks it. The
 * notice, the markup card and the button's own check all read this, so none
 * of them can promise what the button does not do.
 */
function hbcfButtonPreview(scope: ScopeItem[], rule: NonNullable<ReturnType<typeof hbcfRuleContext>>) {
    const list = Array.isArray(scope) ? scope : [];
    const hasHbcf = (rows: ScopeItem[]) => rows.some(item => isManagedHbcfRow(item) || managedScopeItemIncludesHbcf(item));
    const incView = rule.isGstInclusive
        ? list
        : convertScopePricingDisplayBasis(list, { fromGstInclusive: false, toGstInclusive: true });
    let reconciled: any = null;
    try {
        reconciled = reconcileFrancisStatutoryScope(incView, { methodology: rule.methodology, compliancePosture: rule.compliancePosture });
    } catch {
        reconciled = null;
    }
    const blocked = !reconciled || !Array.isArray(reconciled.scopeItems) || String(reconciled.status || '').includes('collision');
    const statutoryTotal = (rows: ScopeItem[]) => rows
        .filter(isManagedStatutoryRow)
        .reduce((sum, item) => sum + Math.max(0, amountNumber(item?.amount)), 0);
    return {
        blocked,
        hasNow: hasHbcf(list),
        hasAfter: blocked ? hasHbcf(list) : hasHbcf(reconciled.scopeItems),
        deltaIncGst: blocked ? 0 : (roundCents(statutoryTotal(reconciled.scopeItems) - statutoryTotal(incView)) || 0),
    };
}

/**
 * The HBCF and compliance rule applied to items on their own GST basis. The
 * methodology and its threshold work on inc-GST prices, as Stage 3 builds
 * them. On an ex-GST Stage 4 the rule runs on the inc-GST view of the items,
 * and only the HBCF and compliance rows it adds or reprices are converted
 * back; every work row comes back exactly as it was (the reconcile keeps
 * their identity). Without this the HBCF button read $19,500 ex GST as
 * $19,500 inc and added nothing, while the quote's pricing check, on the
 * quote's own basis, still refused the quote for a missing HBCF line
 * (review, 2026-10-23).
 */
export function reconcileStatutoryRowsOnScopeBasis(
    scope: ScopeItem[],
    {
        methodology,
        compliancePosture = null,
        hbcfWaived = false,
        isGstInclusive = true,
    }: { methodology: any; compliancePosture?: any; hbcfWaived?: boolean; isGstInclusive?: boolean },
): any {
    const source = Array.isArray(scope) ? scope : [];
    if (isGstInclusive !== false) {
        return reconcileFrancisStatutoryScope(source, { methodology, compliancePosture, hbcfWaived });
    }
    const incView = convertScopePricingDisplayBasis(source, { fromGstInclusive: false, toGstInclusive: true });
    const statutory = reconcileFrancisStatutoryScope(incView, { methodology, compliancePosture, hbcfWaived });
    if (!Array.isArray(statutory?.scopeItems)) return statutory;
    const originalOf = new Map(incView.map((row, index) => [row, source[index]]));
    return {
        ...statutory,
        scopeItems: statutory.scopeItems.map((row: ScopeItem) => (
            originalOf.has(row)
                ? originalOf.get(row)
                : convertScopePricingDisplayBasis([row], { fromGstInclusive: true, toGstInclusive: false })[0]
        )),
    };
}

export interface AssistantHbcfCrossing {
    /** 'over': the work total went over the threshold; 'under': back to it or below. */
    direction: 'over' | 'under';
    message: string;
    /** The notice's one button. It applies the HBCF rule (handleSetBuilderHbcfDecision(false)). */
    actionLabel: 'Add HBCF' | 'Remove HBCF';
}

/**
 * The notice after an Assistant change moves the work total across the HBCF
 * threshold (Peter, 2026-10-23: "only when the AI assistant changes the amount
 * it should check for HBCF and notify the user about it if the maount changed
 * from below the treshold to above or above the $20k threshold to below").
 * Nothing is added or removed here: the notice's button applies the HBCF rule
 * through the builder's own HBCF decision. Null when the threshold is not
 * crossed, when the items already follow the rule at the new total, or when
 * the builder waived HBCF or carries it inside the items.
 */
export function describeAssistantHbcfCrossing(
    beforeScope: ScopeItem[],
    afterScope: ScopeItem[],
    {
        costingConfig,
        formData,
        isGstInclusive = true,
        compliancePosture = null,
    }: { costingConfig?: Record<string, any> | null; formData?: Record<string, any> | null; isGstInclusive?: boolean; compliancePosture?: any } = {},
): AssistantHbcfCrossing | null {
    const rule = hbcfRuleContext({ costingConfig, formData, isGstInclusive, compliancePosture });
    if (!rule) return null;
    const before = Array.isArray(beforeScope) ? beforeScope : [];
    const after = Array.isArray(afterScope) ? afterScope : [];
    const requiredBefore = rule.required(before);
    const requiredAfter = rule.required(after);
    if (!!requiredBefore.required === !!requiredAfter.required) return null;
    // Only a notice whose button would do what it says: a manual HBCF line
    // (which the button refuses, and the pricing check accepts) or items that
    // already follow the rule get none.
    const preview = hbcfButtonPreview(after, rule);
    if (preview.blocked) return null;
    const threshold = `${audMoney(Number(requiredAfter.thresholdIncGst) || 0)} inc GST`;
    if (requiredAfter.required && !preview.hasNow && preview.hasAfter) {
        return {
            direction: 'over',
            message: `The Assistant's change takes the work total over the HBCF threshold of ${threshold}, and the items have no HBCF lines. HBCF is not added for you.`,
            actionLabel: 'Add HBCF',
        };
    }
    if (!requiredAfter.required && preview.hasNow && !preview.hasAfter) {
        return {
            direction: 'under',
            message: `The Assistant's change takes the work total to the HBCF threshold of ${threshold} or below, and the items still have their HBCF lines. They are not removed for you.`,
            actionLabel: 'Remove HBCF',
        };
    }
    return null;
}

/**
 * What the Assistant's change did to the items, for an HBCF notice it raised:
 * each item whose price it changed (or added) at that price and basis, and
 * each item it removed. The notice belongs to that change (review round 4).
 */
export interface AssistantHbcfChange {
    id: string;
    amount?: number;
    removed?: boolean;
    /** Added with no price yet: it only has to stay on the job. */
    pending?: boolean;
    isGstInclusive: boolean;
}

// An item added without a price, waiting for its cost estimate (the same test
// as isPricePendingItem in assistant-scope-apply.ts, which imports this file).
function isPricePendingAddition(item: ScopeItem): boolean {
    return item?.amountLocked !== true
        && !hasCommittedStage4Price(item)
        && String(item?.amountAuthority || '') !== 'committed'
        && Math.abs(amountNumber(item?.amount)) < 0.005;
}

export function assistantHbcfChangeOf(beforeScope: ScopeItem[], afterScope: ScopeItem[], isGstInclusive = true): AssistantHbcfChange[] {
    const before = Array.isArray(beforeScope) ? beforeScope : [];
    const after = Array.isArray(afterScope) ? afterScope : [];
    const basis = isGstInclusive !== false;
    const beforeById = new Map(before.filter(row => row?.id != null).map(row => [String(row.id), row]));
    const afterIds = new Set(after.filter(row => row?.id != null).map(row => String(row.id)));
    const changed: AssistantHbcfChange[] = [];
    for (const row of after) {
        if (row?.id == null) continue;
        const was = beforeById.get(String(row.id));
        const amount = amountNumber(row.amount);
        // An item added with no price adds nothing to the crossing; its later
        // price is judged on its own, so the notice survives it (round 5).
        if (!was && isPricePendingAddition(row)) changed.push({ id: String(row.id), pending: true, isGstInclusive: basis });
        else if (!was || Math.abs(amountNumber(was.amount) - amount) >= 0.005) changed.push({ id: String(row.id), amount, isGstInclusive: basis });
    }
    for (const id of beforeById.keys()) {
        if (!afterIds.has(id)) changed.push({ id, removed: true, isGstInclusive: basis });
    }
    return changed;
}

/**
 * The items on screen still hold the Assistant's change: every item it priced
 * is there at that price (in either GST basis, so a GST switch keeps the
 * notice), and every item it removed is still gone. A builder who kept the
 * other version of the items, or changed one of those items since, no longer
 * has the change the notice describes.
 */
export function scopeCarriesAssistantChange(scope: ScopeItem[], change: AssistantHbcfChange[] | null | undefined): boolean {
    if (!Array.isArray(change) || change.length === 0) return true;
    const list = Array.isArray(scope) ? scope : [];
    const byId = new Map(list.filter(row => row?.id != null).map(row => [String(row.id), row]));
    return change.every((entry) => {
        const row = byId.get(String(entry.id));
        if (entry.removed) return !row;
        if (entry.pending) return !!row;
        if (!row || !Number.isFinite(Number(entry.amount))) return false;
        const now = amountNumber(row.amount);
        const amount = Number(entry.amount);
        if (Math.abs(now - amount) < 0.005) return true;
        const other = amountNumber(convertScopePricingDisplayBasis(
            [{ ...row, amount: amount.toFixed(2) }],
            { fromGstInclusive: entry.isGstInclusive !== false, toGstInclusive: entry.isGstInclusive === false },
        )[0]?.amount);
        return Math.abs(now - other) < 0.005;
    });
}

/** The job and load an HBCF notice was raised for, or the one on screen. */
export interface HbcfNoticeJob {
    projectId?: unknown;
    loadGeneration?: unknown;
}

/**
 * An HBCF notice still belongs to the job on screen: the same project and
 * load, or a new project that got its id on its first save (no id became an
 * id at the same load generation, which the mirror also treats as one job).
 * Review round 2, 2026-10-23: the first save used to clear the notice.
 */
export function isSameHbcfNoticeJob(raisedFor: HbcfNoticeJob | null | undefined, painted: HbcfNoticeJob | null | undefined): boolean {
    return (Object.is(raisedFor?.projectId, painted?.projectId)
        || (raisedFor?.projectId == null && painted?.projectId != null))
        && raisedFor?.loadGeneration === painted?.loadGeneration;
}

/**
 * The toast after the job on screen changed: an HBCF notice for another job
 * goes, one for this job stays, re-keyed to the id a first save gave it.
 * Any other toast is left as it is.
 */
export function hbcfNoticeAfterJobChange<T extends object | null | undefined>(toast: T, painted: HbcfNoticeJob): T | null {
    const raisedFor = (toast as { hbcfNoticeFor?: HbcfNoticeJob | null } | null | undefined)?.hbcfNoticeFor;
    if (!raisedFor) return toast;
    if (!isSameHbcfNoticeJob(raisedFor, painted)) return null;
    return Object.is(raisedFor.projectId, painted.projectId)
        ? toast
        : { ...toast, hbcfNoticeFor: { projectId: painted.projectId, loadGeneration: painted.loadGeneration } } as T;
}

/**
 * A job whose items were typed in Stage 4 has its HBCF first run at its first
 * quote, since no Stage 3 run decided HBCF (Peter, 2026-10-23: "Yes, add HBCF
 * first"). Every job the builder writes by hand is one now: Stage 1 goes
 * straight to the Scope Builder, whatever the job's workflow mode. Only a job
 * whose items the old Stage 3 run built (it has a Work Method) decided HBCF
 * there. True when that first run should add HBCF first: items not built by
 * Stage 3, no AI-written quote yet, no waiver and HBCF not carried in the
 * items, the work total over the threshold, no HBCF lines, and no manual HBCF
 * line the builder's HBCF decision would refuse.
 */
export function firstRunNeedsHbcf(
    scope: ScopeItem[],
    {
        itemsBuiltByWorkMethod = false,
        quoteData = null,
        costingConfig,
        formData,
        isGstInclusive = true,
        compliancePosture = null,
    }: { itemsBuiltByWorkMethod?: boolean; quoteData?: Record<string, any> | null; costingConfig?: Record<string, any> | null; formData?: Record<string, any> | null; isGstInclusive?: boolean; compliancePosture?: any },
): boolean {
    if (itemsBuiltByWorkMethod === true) return false;
    if (formData?.hbcfWaived === true || formData?.hbcfIncludedInScope === true) return false;
    // The first run has happened: an AI quote was written, or HBCF was
    // decided. A fallback draft that replaced the quote does not undo it.
    if (typeof formData?.hbcfFirstRunDecidedAt === 'string' && formData.hbcfFirstRunDecidedAt.length > 0) return false;
    const generatedBy = quoteData?.meta?.generatedBy;
    if (typeof generatedBy === 'string' && generatedBy.length > 0 && generatedBy !== 'deterministic-stage5-draft') return false;
    const rule = hbcfRuleContext({ costingConfig, formData, isGstInclusive, compliancePosture });
    if (!rule) return false;
    const preview = hbcfButtonPreview(Array.isArray(scope) ? scope : [], rule);
    return !preview.blocked && !preview.hasNow && preview.hasAfter;
}

/**
 * The HBCF first run of a job written by hand, at the moment its Stage 4 to 5
 * draft opens (R4, docs/old-pipeline-removal/PLAN.md). The old quote writer was
 * its first quote; now the draft is, so opening Stage 5 is the first run.
 * 'add' when HBCF goes in first (firstRunNeedsHbcf), 'record' when the first
 * run needs none, 'none' when it has already happened, the items were built by
 * Stage 3, it has no items yet, it is an imported or recovered quote, or the
 * rule cannot be read (Pricing has not loaded, or the business's own method
 * has a gap), so the first run waits. A
 * job that already has a quote (a draft or a written one) is past its first
 * run, so reopening its quote never changes its price.
 */
export function firstRunHbcfStepAtStage5(
    scope: ScopeItem[],
    options: { itemsBuiltByWorkMethod?: boolean; pricingReady: boolean; quoteData?: Record<string, any> | null; costingConfig?: Record<string, any> | null; formData?: Record<string, any> | null; isGstInclusive?: boolean; compliancePosture?: any },
): 'add' | 'record' | 'none' {
    if (options.itemsBuiltByWorkMethod === true || options.pricingReady !== true) return 'none';
    if (!Array.isArray(scope) || scope.length === 0) return 'none';
    const decidedAt = options.formData?.hbcfFirstRunDecidedAt;
    if (typeof decidedAt === 'string' && decidedAt.length > 0) return 'none';
    const generatedBy = options.quoteData?.meta?.generatedBy;
    if (typeof generatedBy === 'string' && generatedBy.length > 0) return 'none';
    // A quote imported from a document or recovered from a PDF prints what it
    // printed: its prices, HBCF included, are the document's.
    if (isImportedQuote(options.quoteData) || options.quoteData?.importedFrom || options.quoteData?.recoveredFrom) return 'none';
    const builderOwnsHbcf = options.formData?.hbcfWaived === true || options.formData?.hbcfIncludedInScope === true;
    if (!builderOwnsHbcf && !jobPricingMethodology(options.costingConfig)?.hbcf) return 'none';
    return firstRunNeedsHbcf(scope, options) ? 'add' : 'record';
}

/**
 * Why an HBCF notice's button should do nothing now, or null when it should
 * go ahead. The notice can wait on screen while the builder works, so the
 * press re-reads the job: a waiver or "carried in the items" set since, or
 * items that no longer need what the label says, must not be undone or
 * reversed by an old notice.
 */
export function hbcfNoticeRefusal(
    scope: ScopeItem[],
    actionLabel: string,
    {
        costingConfig,
        formData,
        isGstInclusive = true,
        compliancePosture = null,
    }: { costingConfig?: Record<string, any> | null; formData?: Record<string, any> | null; isGstInclusive?: boolean; compliancePosture?: any } = {},
): string | null {
    if (formData?.hbcfWaived === true) return 'HBCF is now waived on this job, so nothing was changed.';
    if (formData?.hbcfIncludedInScope === true) return 'HBCF is now carried inside the items on this job, so nothing was changed.';
    const rule = hbcfRuleContext({ costingConfig, formData, isGstInclusive, compliancePosture });
    if (!rule) return 'The HBCF rule does not apply to this job, so nothing was changed.';
    const preview = hbcfButtonPreview(scope, rule);
    if (preview.blocked) return null; // The HBCF decision itself explains a manual HBCF line.
    if (actionLabel === 'Remove HBCF') {
        if (!preview.hasNow) return 'The items have no HBCF lines now, so nothing was changed.';
        if (preview.hasAfter) return 'The work total is over the HBCF threshold again, so the HBCF lines stay.';
        return null;
    }
    if (preview.hasNow) return 'The items already have their HBCF lines, so nothing was changed.';
    if (!preview.hasAfter) return 'The work total is not over the HBCF threshold now, so nothing was changed.';
    return null;
}

/**
 * A typed single price, rounded the way the Stage 4 amount field rounds it:
 * whole dollars on an inc-GST quote (no stray cents), cents on an ex-GST one.
 */
export function roundStage4DisplayAmount(value: number, isGstInclusive: boolean): number {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return isGstInclusive ? Math.round(n) : (roundCents(n) || 0);
}

/**
 * The Stage 4 inc/ex GST toggle. Every amount is converted to the new basis
 * (a price stays the same price), then App is told the new basis. Returns
 * false and changes nothing when the basis is already the requested one.
 */
export function applyStage4GstMode({
    fromGstInclusive,
    toGstInclusive,
    setScope,
    onGstModeChange,
}: {
    fromGstInclusive: boolean;
    toGstInclusive: boolean;
    setScope?: ((updater: (prev: ScopeItem[]) => ScopeItem[]) => void) | null;
    onGstModeChange?: ((isGstInclusive: boolean) => void) | null;
}): boolean {
    const from = !!fromGstInclusive;
    const to = !!toGstInclusive;
    if (from === to) return false;
    if (typeof setScope === 'function') {
        setScope((prevScope) => convertScopePricingDisplayBasis(prevScope || [], {
            fromGstInclusive: from,
            toGstInclusive: to,
        }));
    }
    if (typeof onGstModeChange === 'function') onGstModeChange(to);
    return true;
}
